import { useQuery } from "react-query";
import { useEffect, useRef, useState } from 'react';
import { useSearchContext } from "../contexts/SearchContext";
import * as apiClient from "../api-client";
import SearchResultsCard from "../components/SearchResultsCard";
import SearchResultsSkeleton from "../components/Skeletons/SearchResultsSkeleton";
import Pagination from "../components/Pagination";
import StarRatingFilter from "../components/StarRatingFilter";
import HotelTypesFilter from "../components/HotelTypesFilter";
import FacilitiesFilter from "../components/FacilitiesFilter";
import PriceFilter from "../components/PriceFilter";
import PageLoaderComponent from '../components/Common/PageLoaderSearch';
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet";

const Search = () => {
  const search = useSearchContext();
  const [page, setPage] = useState<number>(1);
  const [selectedStars, setSelectedStars] = useState<string[]>([]);
  const [selectedHotelTypes, setSelectedHotelTypes] = useState<string[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedPrice, setSelectedPrice] = useState<number | undefined>();
  const [sortOption, setSortOption] = useState<string>("");
  const [displayLoader, setDisplayLoader] = useState<boolean>(true);

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('card-visible');
          } else {
            entry.target.classList.remove('card-visible');
          }
        });
      },
      {
        rootMargin: '0px',
        threshold: 0.5,
      }
    );

    cardRefs.current.forEach((ref) => ref && observer.observe(ref));

    return () => {
      cardRefs.current.forEach((ref) => ref && observer.unobserve(ref));
    };
  }, []);

  const searchParams = {
    destination: search.destination,
    checkIn: search.checkIn.toISOString(),
    checkOut: search.checkOut.toISOString(),
    adultCount: search.adultCount.toString(),
    childCount: search.childCount.toString(),
    page: page.toString(),
    stars: selectedStars,
    types: selectedHotelTypes,
    facilities: selectedFacilities,
    maxPrice: selectedPrice?.toString(),
  };

  useEffect(() => {
    setDisplayLoader(true);
    setPage(1);
  }, [searchParams.destination, searchParams.checkIn, searchParams.checkOut, searchParams.adultCount, searchParams.childCount, selectedStars, selectedHotelTypes, selectedFacilities, selectedPrice]);


  const { data: hotelData, isLoading } = useQuery(["searchHotels", searchParams], () => {
    return apiClient.searchHotels(searchParams).finally(() => {
      // Use a timeout to control loader display duration
      const timeoutId = setTimeout(() => {
        setDisplayLoader(false); // Hide loader after a delay
      }, 5000); // Adjust the delay as needed (1000 ms = 1 second)

      return () => clearTimeout(timeoutId); // Cleanup timeout on unmount
    });
  });

  const handleStarsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const starRating = event.target.value;
    setSelectedStars((prevStars) =>
      event.target.checked
        ? [...prevStars, starRating]
        : prevStars.filter((star) => star !== starRating)
    );
  };

  const handleHotelTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const hotelType = event.target.value;
    setSelectedHotelTypes((prevHotelTypes) =>
      event.target.checked
        ? [...prevHotelTypes, hotelType]
        : prevHotelTypes.filter((hotel) => hotel !== hotelType)
    );
  };

  const handleFacilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const facility = event.target.value;
    setSelectedFacilities((prevFacilities) =>
      event.target.checked
        ? [...prevFacilities, facility]
        : prevFacilities.filter((prevFacility) => prevFacility !== facility)
    );
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Search Holidays & Travel Deals | Halo Holidays</title>
          <meta name="description" content="Find your perfect getaway with our powerful search tool. Explore destinations, compare prices, and discover amazing travel deals with Halo Holidays." />
        </Helmet>
      </HelmetProvider>

      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-5 px-6 md:px-20 lg:px-0">
        {displayLoader && (
          <PageLoaderComponent
            checkIn={search.checkIn.toISOString().split('T')[0]}
            checkOut={search.checkOut.toISOString().split('T')[0]}
          />
        )}
        {/* Filters Sidebar */}
        <div className="rounded-lg border border-slate-300 p-5 h-fit md:sticky hidden md:block top-10">
          <div className="space-y-6">
            <h3 className="text-lg font-semibold border-b border-slate-300 pb-5">
              Filter by:
            </h3>
            <StarRatingFilter
              selectedStars={selectedStars}
              onChange={handleStarsChange}
            />
            <HotelTypesFilter
              selectedHotelTypes={selectedHotelTypes}
              onChange={handleHotelTypeChange}
            />
            <FacilitiesFilter
              selectedFacilities={selectedFacilities}
              onChange={handleFacilityChange}
            />
            <PriceFilter
              selectedPrice={selectedPrice}
              onChange={(value) => setSelectedPrice(value)}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col md:flex-row md:justify-between items-center">
            <span className="text-lg font-bold text-center md:text-left mt-2">
              {hotelData?.pagination.total} Hotels Found
              {search.destination ? ` in ${search.destination.charAt(0).toUpperCase() + search.destination.slice(1)}` : ""}
              {hotelData?.pagination.total !== 0 && ' That Will Make You Say "Ooh La La!"'}
            </span>
            <select
              value={sortOption}
              onChange={(event) => setSortOption(event.target.value)}
              className="p-2 border rounded-md mt-4 md:mt-0 w-full md:w-auto"
            >
              <option value="">Sort By</option>
              <option value="starRating">Star Rating</option>
              <option value="pricePerNightAsc">
                Price Per Night (low to high)
              </option>
              <option value="pricePerNightDesc">
                Price Per Night (high to low)
              </option>
            </select>
          </div>

          {/* Hotel Cards */}
          <div className="flex flex-col">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <SearchResultsSkeleton key={index} />
              ))
            ) : (
              hotelData?.data.map((hotel, index) => (
                <div key={hotel._id} ref={(el) => (cardRefs.current[index] = el)}>
                  <SearchResultsCard hotel={hotel} />
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          <Pagination
            page={hotelData?.pagination.page || 1}
            pages={hotelData?.pagination.pages || 1}
            onPageChange={(page) => setPage(page)}
          />
        </div>
      </div>
    </>
  );
};

export default Search;
